/**
 * MediaImportBatchesPage — Phase A édito (2026-09-21)
 * Gestion des Lots d'importation médias. Additif : ne remplace pas la
 * médiathèque existante (Photos), sert juste à REGROUPER les imports.
 */
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import {
  getMediaImportBatches,
  createMediaImportBatch,
  updateMediaImportBatch,
  deleteMediaImportBatch,
} from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Loader2, Plus, Trash2, Pencil, Layers, Images } from "lucide-react";

function formatDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(); } catch { return String(iso); }
}

export default function MediaImportBatchesPage() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [openCreate, setOpenCreate] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", date: "", description: "", notes: "" });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getMediaImportBatches();
      setItems(res.data?.items || []);
      setTotal(res.data?.total || 0);
    } catch {
      toast.error("Impossible de charger les lots.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const startCreate = () => {
    setEditing(null);
    setForm({ name: "", date: "", description: "", notes: "" });
    setOpenCreate(true);
  };

  const startEdit = (row) => {
    setEditing(row);
    setForm({
      name: row.name || "",
      date: row.date || "",
      description: row.description || "",
      notes: row.notes || "",
    });
    setOpenCreate(true);
  };

  const open = (row) => {
    window.location.href = `/dashboard/medias/lots/${row.id}`;
  };

  const submit = async () => {
    if (!form.name || form.name.trim().length < 2) {
      toast.error("Le nom du lot doit contenir au moins 2 caractères.");
      return;
    }
    try {
      if (editing) {
        await updateMediaImportBatch(editing.id, form);
        toast.success("Lot mis à jour.");
      } else {
        await createMediaImportBatch(form);
        toast.success("Lot créé.");
      }
      setOpenCreate(false);
      await load();
    } catch (e) {
      toast.error("Erreur : " + (e?.response?.data?.detail || e?.message || "unknown"));
    }
  };

  const remove = async (row) => {
    if (!window.confirm(`Supprimer le lot « ${row.name} » ?\n\nLes médias qu'il contient seront conservés (le lien vers ce lot sera juste retiré).`)) return;
    try {
      await deleteMediaImportBatch(row.id);
      toast.success("Lot supprimé (médias conservés).");
      await load();
    } catch (e) {
      toast.error("Erreur : " + (e?.response?.data?.detail || e?.message || "unknown"));
    }
  };

  return (
    <div data-testid="media-batches-page" className="max-w-5xl">
      <header className="mb-6 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-heading text-3xl text-primary flex items-center gap-3">
            <Layers className="h-7 w-7" />
            Lots d'importation médias
          </h1>
          <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
            Regroupez chaque campagne d'import massif de photos sous un lot nommé (ex. « MSC Meraviglia — septembre 2026 »).
            Les médias existants (sans lot) restent parfaitement valides.
          </p>
        </div>
        <Button onClick={startCreate} data-testid="media-batches-create-btn">
          <Plus className="h-4 w-4 mr-2" />
          Nouveau lot
        </Button>
      </header>

      <div className="bg-white border border-border rounded-lg overflow-hidden">
        {loading ? (
          <div className="p-12 flex items-center justify-center text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin mr-2" /> Chargement…
          </div>
        ) : items.length === 0 ? (
          <div className="p-12 text-center text-sm text-muted-foreground">
            Aucun lot pour l'instant. Créez-en un pour organiser vos prochains imports.
          </div>
        ) : (
          <table className="w-full text-sm" data-testid="media-batches-table">
            <thead className="bg-secondary/50 text-xs uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="text-left px-6 py-3">Nom</th>
                <th className="text-left px-6 py-3">Date</th>
                <th className="text-left px-6 py-3">Médias</th>
                <th className="text-left px-6 py-3">Créé le</th>
                <th className="text-right px-6 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((row) => (
                <tr key={row.id} className="border-t border-border" data-testid={`media-batch-row-${row.id}`}>
                  <td className="px-6 py-3 font-medium">
                    <button
                      className="text-primary hover:underline text-left"
                      onClick={() => open(row)}
                      data-testid={`media-batch-open-${row.id}`}
                    >
                      {row.name}
                    </button>
                  </td>
                  <td className="px-6 py-3 text-muted-foreground">{row.date || "—"}</td>
                  <td className="px-6 py-3">
                    <span className="inline-flex items-center gap-1 text-primary text-xs font-medium">
                      <Images className="h-4 w-4" /> {row.media_count ?? 0}
                    </span>
                  </td>
                  <td className="px-6 py-3 text-muted-foreground">{formatDate(row.created_at)}</td>
                  <td className="px-6 py-3 text-right">
                    <Button variant="ghost" size="sm" onClick={() => open(row)} data-testid={`media-batch-open-btn-${row.id}`}>
                      Ouvrir
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => startEdit(row)} data-testid={`media-batch-edit-${row.id}`}>
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => remove(row)} data-testid={`media-batch-delete-${row.id}`}>
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {total > 0 && (
          <div className="p-3 text-xs text-muted-foreground border-t border-border">
            {items.length} sur {total}
          </div>
        )}
      </div>

      <Dialog open={openCreate} onOpenChange={setOpenCreate}>
        <DialogContent data-testid="media-batch-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? "Modifier le lot" : "Nouveau lot d'importation"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium">Nom *</label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Ex. MSC Meraviglia — septembre 2026"
                data-testid="media-batch-input-name"
              />
            </div>
            <div>
              <label className="text-sm font-medium">Date de la campagne</label>
              <Input
                type="date"
                value={form.date}
                onChange={(e) => setForm({ ...form, date: e.target.value })}
                data-testid="media-batch-input-date"
              />
            </div>
            <div>
              <label className="text-sm font-medium">Description</label>
              <Textarea
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="Contexte, événement, sujet du reportage…"
                data-testid="media-batch-input-description"
              />
            </div>
            <div>
              <label className="text-sm font-medium">Notes internes</label>
              <Textarea
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                data-testid="media-batch-input-notes"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenCreate(false)}>Annuler</Button>
            <Button onClick={submit} data-testid="media-batch-submit">
              {editing ? "Enregistrer" : "Créer le lot"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
